import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react'
import { useStore } from '@moonpig/web-core-stores'
import {
  createGenericSelectContentEvent,
  useTrackGAEventOnce,
  trackGAEvent,
} from '@moonpig/web-core-analytics'
import { dispatcherContext, eventContext } from './Context'
import type { Dispatcher, ProductModalClosed, ProductModalShown } from './types'
import { EMPTY_STATE } from './types'
import { ProductModal } from '../../components/ProductModal'
import type { ProductContext, ProductInfoProduct } from '../../types'
import { OnConfigureModulesCallback } from '../../modules/types'
import { useDefaultModuleConfiguration } from '../../modules/configurations'
import { useRecentlyViewedDispatcher } from '../recentlyViewed'
import {
  createProductDetailsScreenView,
  createViewItemsWithAddonsGAEvent,
} from '../../analytics'
import { useCanNavigateInModal } from '../../utils/useCanNavigateInModal'
import { useProductModalHistory } from './useProductModalHistory'
import { modalStateReducer } from './Reducer'

export const ProductModalProvider: FC<
  React.PropsWithChildren<{
    context: ProductContext
    onConfigureModules?: OnConfigureModulesCallback
  }>
> = ({ context, children, onConfigureModules }) => {
  const [state, dispatch] = useReducer(modalStateReducer, EMPTY_STATE)
  const { addToRecentlyViewed } = useRecentlyViewedDispatcher()
  const configureModules = useDefaultModuleConfiguration()
  const { store } = useStore()
  const { trackGAEventOnce } = useTrackGAEventOnce()
  const canNavigateInModal = useCanNavigateInModal()
  const [event, setEvent] = useState<
    ProductModalClosed | ProductModalShown | null
  >(null)

  const restoreProduct = useCallback((product: ProductInfoProduct) => {
    dispatch({ type: 'RESTORE_PRODUCT', product })
  }, [])

  const leaveModal = useCallback(() => {
    setEvent({ type: 'PRODUCT_MODAL_CLOSED' })
  }, [])

  const { pushProduct, closeHistory } = useProductModalHistory({
    canNavigateInModal,
    onRestoreProduct: restoreProduct,
    onLeaveModal: leaveModal,
  })

  const dismiss = useCallback(() => {
    trackGAEvent(
      createGenericSelectContentEvent({
        eventCategory: `${context.pageType} action`,
        eventAction: 'close',
        eventLabel: 'product details',
        itemId: state.product?.id,
      }),
    )

    setEvent({ type: 'PRODUCT_MODAL_CLOSED' })
    closeHistory()
  }, [closeHistory, context.pageType, state.product?.id])

  const actions = useMemo<Dispatcher>(
    () => ({
      show: params => {
        dispatch({ type: 'SHOW', payload: params })
        setEvent({ type: 'PRODUCT_MODAL_SHOWN' })
        addToRecentlyViewed(params)
        pushProduct(params.product)
      },

      showProduct: product => {
        dispatch({ type: 'SHOW_PRODUCT', product })
        addToRecentlyViewed({ product })
        pushProduct(product)
      },

      close: dismiss,
    }),
    [addToRecentlyViewed, dismiss, pushProduct],
  )

  useEffect(() => {
    if (!state.isOpen || !state.product) {
      return
    }

    const product = state.product
    const {
      id,
      category,
      title,
      masterVariant,
      isSponsored,
      internallyPromoted,
    } = product

    trackGAEventOnce(createProductDetailsScreenView({ store, product }))
    trackGAEventOnce(
      createViewItemsWithAddonsGAEvent({
        product: {
          id,
          category: category.name,
          title,
          price: masterVariant.price.centAmount,
          index: 0,
          categoryDepartment: category.department,
          inStock: masterVariant.inStock,
          isSponsored,
          internallyPromoted,
          dynamicImages: product.dynamicImages,
        },
        variants: product.variants,
        pageLocation: 'product details',
      }),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isOpen])

  useEffect(() => {
    if (event?.type !== 'PRODUCT_MODAL_CLOSED') {
      return
    }

    const trigger = state.triggerElementRef?.current

    if (trigger) {
      setTimeout(() => trigger.focus(), 0)
    }

    dispatch({ type: 'CLOSE' })
  }, [event, state.triggerElementRef])

  return (
    <dispatcherContext.Provider value={actions}>
      <eventContext.Provider value={event}>
        {children}

        {state.product && (
          <ProductModal
            {...state}
            product={state.product}
            upsellProduct={state.upsellProduct}
            productContext={{
              ...context,
              onTrackingInfo: state.onTrackingInfo,
            }}
            onConfigureModules={onConfigureModules ?? configureModules}
            onDismiss={dismiss}
          />
        )}
      </eventContext.Provider>
    </dispatcherContext.Provider>
  )
}
